import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Button, Card, CardHeader, CardTitle, CardDescription, CardContent, Input, DatePicker, Modal } from '@/components/ui';
import MarketTypeahead from '@/components/ui/MarketTypeahead';
import MediaFormatTypeahead from '@/components/ui/MediaFormatTypeahead';
import { getBusinessCategorySuggestions, requiresMoviesGap } from '@/lib/business-categories';
import { env } from '@/lib/env';
import { getLocalTodayYMD, serializeHoldExpirationDate, parseDateToLocalYMD } from '@/lib/utils';
import { useSalesMarkets } from '@/hooks/useSalesMarkets';
import { useMediaFormats } from '@/hooks/useMediaFormats';
import { useValidateProductId } from '@/hooks/useInventory';
import { useShowingTypes } from '@/hooks/useShowingTypes';
import { resolveMarketCode } from '@/lib/market-filter';
import { resolveMediaFormatId } from '@/lib/media-format-filter';
import { calculateEndDate as calcEndDate, calculateStartDate as calcStartDate } from '@/lib/date-period-calc';
import { useFieldValidation } from '@/components/forms/useFieldValidation';
import { ErrorIndicator } from '@/components/forms/ErrorIndicator';
import { LoadingSpinner } from '@/components/common';
import { addBusinessDays, startOfDay, format } from 'date-fns';
import type { ShowingType } from '@/types';

type LineItemInputTab = 'upload' | 'paste' | 'manual';

// localStorage key for form data persistence (survives tab close)
const FORM_STORAGE_KEY = 'holdRequestFormDraft';
const FORM_DRAFT_TTL_MS = 4 * 60 * 60 * 1000; // 4 hours

interface GridCell {
  value: string;
  error?: string;
}

interface GridRow {
  id: string;
  cells: GridCell[];
  rowNumber: number;
}

interface ValidationError {
  rowNumber: number;
  field: string;
  message: string;
}

interface ProcessedRow {
  rowNumber: number;
  success: boolean;
  errors: ValidationError[];
}

interface UploadErrors {
  totalRows: number;
  failedRows: number;
  successfulRows?: number;
  errors: ValidationError[];
  processedRows?: ProcessedRow[];
}

interface HoldRequestFormData {
  advertiser: string;
  businessCategory: string;
  isCrossRegion: boolean;
  serviceAeId?: string;
  holdStartDate?: string; // YYYY-MM-DD from client's local timezone
  holdExpirationDate?: string;
}

interface UnifiedHoldRequestFormProps {
  onSubmit: (data: { holdRequest: HoldRequestFormData; lineItems: Record<string, string | number | boolean | undefined>[] }) => Promise<void>;
  isProcessing?: boolean;
  onCancel?: () => void;
  initialData?: { holdRequest: HoldRequestFormData; lineItems: Record<string, string | number | boolean | undefined>[] } | null;
  uploadErrors?: UploadErrors | null;
  onDismissErrors?: () => void;
}

const LINE_ITEM_COLUMNS = [
  { key: 'market', label: 'Market', required: true },
  { key: 'mediaType', label: 'Media Type', required: true },
  { key: 'unitId', label: 'Unit ID / Product ID', required: false },
  { key: 'coverage', label: 'Coverage', required: false },
  { key: 'qty', label: 'Qty', required: false },
  { key: 'digitalOnlyNoOfSpots', label: 'Digital Only - No of Spots', required: false },
  { key: 'negotiatedPerPeriodRate', label: 'Negotiated Per Period Rate', required: false },
  { key: 'startDate', label: 'Start Date', required: true },
  { key: 'noOfPeriods', label: 'No of Periods', required: false },
  { key: 'typeOfPeriod', label: 'Type of Period', required: false },
  { key: 'endDate', label: 'End Date', required: false },
  { key: 'holdExpirationDate', label: 'Hold Expiration Date', required: false },
  { key: 'showingType', label: 'Showing Type', required: false },
  { key: 'isException', label: 'Exception', required: false, type: 'checkbox' },
  { key: 'serviceAeEmail', label: 'Service AE Email', required: false },
  { key: 'notes', label: 'Notes', required: false },
];

// Showing types where net/rate amount must be $0 (OPE-285).
// `satisfies` validates each entry is a real ShowingType at compile time;
// `as const` preserves the literal tuple so ZeroAmountShowingType is exact.
const ZERO_AMOUNT_SHOWING_TYPES = ['guaranteed_bonus', 'space_available_bonus', 'psa'] as const satisfies readonly ShowingType[];

/** The three-value subset of ShowingType that carries no monetary value. */
type ZeroAmountShowingType = typeof ZERO_AMOUNT_SHOWING_TYPES[number];

/**
 * Type guard: true iff value is one of the three zero-amount showing type codes.
 * Narrows to ZeroAmountShowingType (not the full ShowingType union) so callers
 * get the tightest correct type.
 */
function isZeroAmountShowingType(value: string): value is ZeroAmountShowingType {
  return (ZERO_AMOUNT_SHOWING_TYPES as readonly string[]).includes(value);
}

const NEGOTIATED_RATE_COL_INDEX = LINE_ITEM_COLUMNS.findIndex(c => c.key === 'negotiatedPerPeriodRate');
const SHOWING_TYPE_COL_INDEX = LINE_ITEM_COLUMNS.findIndex(c => c.key === 'showingType');

/** Create a default empty cell array */
function createEmptyCells(): GridCell[] {
  return LINE_ITEM_COLUMNS.map(() => ({ value: '' }));
}

export default function UnifiedHoldRequestForm({ onSubmit, isProcessing, onCancel, initialData, uploadErrors, onDismissErrors }: UnifiedHoldRequestFormProps) {
  // Restore saved form data from localStorage on mount (expires after 4 hours)
  const savedFormData = useMemo(() => {
    if (typeof window === 'undefined') return null;
    try {
      const saved = localStorage.getItem(FORM_STORAGE_KEY);
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      // Check TTL — discard if older than 4 hours
      if (parsed._savedAt && Date.now() - parsed._savedAt > FORM_DRAFT_TTL_MS) {
        localStorage.removeItem(FORM_STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }, []);

  // Hold request header fields - restore from localStorage if available
  const [advertiser, setAdvertiser] = useState(
    initialData?.holdRequest?.advertiser || savedFormData?.advertiser || ''
  );
  const [businessCategoryInput, setBusinessCategoryInput] = useState(
    initialData?.holdRequest?.businessCategory || savedFormData?.businessCategory || ''
  );
  const [businessCategory, setBusinessCategory] = useState(
    initialData?.holdRequest?.businessCategory || savedFormData?.businessCategory || ''
  );
  const [isCrossRegion, setIsCrossRegion] = useState(
    initialData?.holdRequest?.isCrossRegion || savedFormData?.isCrossRegion || false
  );
  const [holdRequestDate] = useState<string>(getLocalTodayYMD());
  
  // Calculate default (5 business days) and max (10 business days) hold expiration dates
  const today = useMemo(() => startOfDay(new Date()), []);
  const defaultHoldExpirationDate = useMemo(() => {
    const date = addBusinessDays(today, 5);
    return format(date, 'M/d/yyyy');
  }, [today]);
  const maxHoldExpirationDate = useMemo(() => {
    const date = addBusinessDays(today, 10);
    // Store as YYYY-MM-DD string for timezone-safe comparisons
    return format(date, 'yyyy-MM-dd');
  }, [today]);
  
  // holdExpirationDate — editable, defaults to 5 business days, max 10 business days (unless exception)
  const [holdExpirationDate, setHoldExpirationDate] = useState<string>(
    initialData?.holdRequest?.holdExpirationDate || savedFormData?.holdExpirationDate || defaultHoldExpirationDate
  );
  const [holdExpirationDateError, setHoldExpirationDateError] = useState<string | null>(null);
  const [showBusinessCategorySuggestions, setShowBusinessCategorySuggestions] = useState(false);

  // Fetch sales markets for the typeahead
  const { data: salesMarkets, isLoading: marketsLoading } = useSalesMarkets();

  // Fetch media formats for the typeahead
  const { data: mediaFormats, isLoading: mediaFormatsLoading } = useMediaFormats();

  // Fetch showing types from API
  const { data: showingTypes = [], isLoading: isLoadingShowingTypes } = useShowingTypes();

  // Line items grid — lazy init avoids React 18 StrictMode double-mount bug
  // Default to 3 empty rows for better UX, restore from localStorage if available
  const [rows, setRows] = useState<GridRow[]>(() => {
    // Priority: initialData > savedFormData > default 3 empty rows
    if (initialData?.lineItems && initialData.lineItems.length > 0) {
      return initialData.lineItems.map((item: Record<string, string | number | boolean | undefined>, index: number) => ({
        id: `row-${Date.now()}-${index}`,
        rowNumber: index + 1,
        cells: LINE_ITEM_COLUMNS.map((col) => ({
          value: String(item[col.key] ?? '')
        }))
      }));
    }
    
    // Restore from localStorage
    if (savedFormData?.rows && savedFormData.rows.length > 0) {
      return savedFormData.rows.map((row: { cells?: GridCell[] }, index: number) => ({
        id: `row-${Date.now()}-${index}`,
        rowNumber: index + 1,
        cells: row.cells || createEmptyCells()
      }));
    }
    
    // Create 3 empty rows by default
    return Array.from({ length: 3 }, (_, index) => ({
      id: `row-${Date.now()}-${index}`,
      rowNumber: index + 1,
      cells: createEmptyCells(),
    }));
  });
  
  // Track if submission was successful to disable the button
  const [isSubmitted, setIsSubmitted] = useState(false);
  const pasteAreaRef = useRef<HTMLTextAreaElement>(null);

  // Tab and modal state
  const [activeTab, setActiveTab] = useState<LineItemInputTab>('upload');
  const [showFormattingRules, setShowFormattingRules] = useState(false);

  // Excel upload state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Import info banner state
  const [importInfo, setImportInfo] = useState<{ rowCount: number; format: string } | null>(null);

  // Grid expand state
  const [isGridExpanded, setIsGridExpanded] = useState(false);

  // Exception modal state - show only once per session
  const [showExceptionModal, setShowExceptionModal] = useState(false);
  // Error details modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const hasShownExceptionModal = useRef(
    typeof window !== 'undefined' && sessionStorage.getItem('exceptionModalShown') === 'true'
  );

  // Close expanded grid on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isGridExpanded) {
        setIsGridExpanded(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isGridExpanded]);

  // Prevent body scroll when grid is expanded
  useEffect(() => {
    if (isGridExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isGridExpanded]);

  // Save form data to localStorage when it changes
  useEffect(() => {
    // Don't save if form was just submitted
    if (isSubmitted) return;
    
    // Check if there's any data worth saving
    const hasHeaderData = advertiser || businessCategory || isCrossRegion || holdExpirationDate !== defaultHoldExpirationDate;
    const hasRowData = rows.some(row => row.cells.some(cell => cell.value.trim() !== ''));
    
    if (!hasHeaderData && !hasRowData) {
      // Clear storage if form is empty
      localStorage.removeItem(FORM_STORAGE_KEY);
      return;
    }
    
    // Save to localStorage
    const formData = {
      advertiser,
      businessCategory,
      isCrossRegion,
      holdExpirationDate,
      rows: rows.map(row => ({
        cells: row.cells.map(cell => ({ value: cell.value }))
      })),
      savedAt: new Date().toISOString(),
      _savedAt: Date.now(),
    };
    
    try {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
    } catch (e) {
      console.warn('Failed to save form data to localStorage:', e);
    }
  }, [advertiser, businessCategory, isCrossRegion, holdExpirationDate, defaultHoldExpirationDate, rows, isSubmitted]);

  // Form errors — managed by useFieldValidation hook
  const {
    headerErrors,
    validateHeaderField,
    validateGridCell,
    validateAll,
    clearGridError,
    clearRowErrors,
    getGridError,
    setGridError,
    setBackendErrors,
    clearAllErrors,
  } = useFieldValidation();

  // Column indices for end date auto-calculation
  const COL_START_DATE = 7;
  const COL_NO_OF_PERIODS = 8;
  const COL_TYPE_OF_PERIOD = 9;
  const COL_END_DATE = 10;

  /**
   * Calculate end date from start date + number of periods + type of period.
   * End dates are inclusive (start day counts as day 1 of the period).
   * Matches the backend logic in template-processing-service.ts.
   */
  const calculateEndDate = useCallback((startDateStr: string, noOfPeriodsStr: string, typeOfPeriod: string): string => {
    return calcEndDate(startDateStr, noOfPeriodsStr, typeOfPeriod);
  }, []);

  /**
   * Calculate start date from end date + number of periods + type of period (reverse calculation).
   * End dates are inclusive (start day counts as day 1 of the period).
   */
  const calculateStartDate = useCallback((endDateStr: string, noOfPeriodsStr: string, typeOfPeriod: string): string => {
    return calcStartDate(endDateStr, noOfPeriodsStr, typeOfPeriod);
  }, []);

  // handleCellChange must be defined before useEffects that reference it
  const handleCellChange = useCallback((rowId: string, colIndex: number, value: string) => {
    clearGridError(rowId, colIndex);
    setRows(prev => prev.map(row => {
      if (row.id === rowId) {
        const newCells = [...row.cells];
        newCells[colIndex] = { value, error: undefined };

        // Auto-zero negotiated rate when a zero-amount showing type is selected
        if (colIndex === SHOWING_TYPE_COL_INDEX && isZeroAmountShowingType(value)) {
          if (NEGOTIATED_RATE_COL_INDEX >= 0) {
            newCells[NEGOTIATED_RATE_COL_INDEX] = { value: '0', error: undefined };
          }
        }

        // Auto-calculate end date when startDate, noOfPeriods, or typeOfPeriod changes
        if (colIndex === COL_START_DATE || colIndex === COL_NO_OF_PERIODS || colIndex === COL_TYPE_OF_PERIOD) {
          const startDate = colIndex === COL_START_DATE ? value : (newCells[COL_START_DATE]?.value ?? '');
          const noOfPeriods = colIndex === COL_NO_OF_PERIODS ? value : (newCells[COL_NO_OF_PERIODS]?.value || '1');
          const typeOfPeriod = colIndex === COL_TYPE_OF_PERIOD ? value : (newCells[COL_TYPE_OF_PERIOD]?.value || '4wks');
          if (startDate && noOfPeriods && typeOfPeriod) {
            const calculatedEnd = calculateEndDate(startDate, noOfPeriods, typeOfPeriod);
            if (calculatedEnd) {
              newCells[COL_END_DATE] = { value: calculatedEnd, error: undefined };
              clearGridError(rowId, COL_END_DATE);
            } else {
              newCells[COL_END_DATE] = { value: '', error: undefined };
              clearGridError(rowId, COL_END_DATE);
            }
          } else if (colIndex === COL_START_DATE && !startDate) {
            // Clear end date when start date is cleared
            newCells[COL_END_DATE] = { value: '', error: undefined };
            clearGridError(rowId, COL_END_DATE);
          }
        }

        // Auto-calculate start date when endDate changes (reverse calculation)
        // Uses UI defaults ('1', '4wks') — dates stay linked via period
        if (colIndex === COL_END_DATE) {
          const endDate = value;
          const noOfPeriods = newCells[COL_NO_OF_PERIODS]?.value || '1';
          const typeOfPeriod = newCells[COL_TYPE_OF_PERIOD]?.value || '4wks';
          if (endDate) {
            const calculatedStart = calculateStartDate(endDate, noOfPeriods, typeOfPeriod);
            if (calculatedStart) {
              newCells[COL_START_DATE] = { value: calculatedStart, error: undefined };
              clearGridError(rowId, COL_START_DATE);
            } else {
              newCells[COL_START_DATE] = { value: '', error: undefined };
              clearGridError(rowId, COL_START_DATE);
            }
          } else {
            // Clear start date when end date is cleared
            newCells[COL_START_DATE] = { value: '', error: undefined };
            clearGridError(rowId, COL_START_DATE);
          }
        }

        return { ...row, cells: newCells };
      }
      return row;
    }));
  }, [clearGridError, calculateEndDate, calculateStartDate]);

  // Unit ID validation state for grid rows
  const [gridValidationId, setGridValidationId] = useState('');
  const [gridValidationRowId, setGridValidationRowId] = useState('');
  const [gridValidationMarket, setGridValidationMarket] = useState<string | undefined>(undefined);
  const {
    data: gridValidationResult,
    isLoading: gridIsValidating,
    isFetching: gridIsFetching,
  } = useValidateProductId(
    gridValidationId || '',
    gridValidationMarket // pass row's market when available, undefined for cross-market
  );

  // Auto-populate market cell when grid validation resolves a single match
  useEffect(() => {
    if (gridValidationResult?.valid && gridValidationResult.matchedMarket && gridValidationRowId) {
      const resolved = salesMarkets?.find(
        (m) => m.market_name.toLowerCase() === gridValidationResult.matchedMarket!.toLowerCase()
      );
      if (resolved) {
        // Only auto-populate if the market cell is currently empty
        const row = rows.find((r) => r.id === gridValidationRowId);
        if (row && !row.cells[0]?.value) {
          handleCellChange(gridValidationRowId, 0, resolved.market_code);
        }
      }
    }
  }, [gridValidationResult, gridValidationRowId, salesMarkets, rows, handleCellChange]);

  // Set/clear grid error on unit ID cell based on validation result (shows ErrorIndicator icon)
  useEffect(() => {
    if (!gridValidationRowId) return;
    const unitIdColIndex = 2; // Unit ID is column index 2
    if (gridValidationResult && !gridValidationResult.valid && !gridValidationResult.needsDisambiguation && gridValidationResult.error) {
      setGridError(gridValidationRowId, unitIdColIndex, gridValidationResult.error);
    } else if (gridValidationResult?.valid || gridValidationResult?.needsDisambiguation) {
      clearGridError(gridValidationRowId, unitIdColIndex);
    }
  }, [gridValidationResult, gridValidationRowId, setGridError, clearGridError]);

  // Handle Unit ID cell blur in the grid
  const handleGridUnitIdBlur = useCallback(
    (rowId: string, value: string, rowMarket: string) => {
      const trimmed = value.trim();
      if (trimmed) {
        setGridValidationId(trimmed);
        setGridValidationRowId(rowId);
        setGridValidationMarket(rowMarket.trim() || undefined);
      } else {
        if (gridValidationRowId === rowId) {
          setGridValidationId('');
          setGridValidationRowId('');
          setGridValidationMarket(undefined);
        }
      }
    },
    [gridValidationRowId]
  );

  // Handle disambiguation selection in the grid
  const handleGridDisambiguationSelect = useCallback(
    (rowId: string, marketName: string) => {
      const resolved = salesMarkets?.find(
        (m) => m.market_name.toLowerCase() === marketName.toLowerCase()
      );
      if (resolved) {
        handleCellChange(rowId, 0, resolved.market_code);
        // Clear disambiguation state — the market cell is now set, user can re-validate manually
        setGridValidationId('');
        setGridValidationRowId('');
      }
    },
    [salesMarkets, handleCellChange]
  );

  // Apply backend validation errors to grid cells when uploadErrors changes
  // Use a ref to track the last processed uploadErrors to avoid re-running on row changes
  const lastUploadErrorsRef = useRef<UploadErrors | null>(null);
  useEffect(() => {
    if (uploadErrors && uploadErrors.errors.length > 0 && uploadErrors !== lastUploadErrorsRef.current) {
      lastUploadErrorsRef.current = uploadErrors;
      setBackendErrors(uploadErrors.errors, rows);
    } else if (!uploadErrors && lastUploadErrorsRef.current) {
      lastUploadErrorsRef.current = null;
    }
  }, [uploadErrors, rows, setBackendErrors]);

  // Clear errors when dismissing the error panel
  const handleDismissErrors = useCallback(() => {
    clearAllErrors();
    onDismissErrors?.();
  }, [clearAllErrors, onDismissErrors]);  // Get business category suggestions
  const businessCategorySuggestions = useMemo(() => {
    return getBusinessCategorySuggestions(businessCategoryInput);
  }, [businessCategoryInput]);

  const addEmptyRow = () => {
    setRows(prev => {
      const newRow: GridRow = {
        id: `row-${Date.now()}-${Math.random()}`,
        rowNumber: prev.length + 1,
        cells: createEmptyCells(),
      };
      return [...prev, newRow];
    });
  };

  const _handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    if (!pastedText.trim()) return;

    // Parse pasted data (tab-separated or comma-separated)
    const lines = pastedText.split('\n').filter(line => line.trim());

    // Detect and skip header row — if the first cell matches a column label, it's a header
    const columnLabels = LINE_ITEM_COLUMNS.map(col => col.label.toLowerCase());
    const firstLine = lines[0] || '';
    const firstLineCells = firstLine.includes('\t')
      ? firstLine.split('\t')
      : firstLine.split(',').map(c => c.trim());
    const firstCellNormalized = firstLineCells[0]?.trim().toLowerCase() || '';
    if (columnLabels.includes(firstCellNormalized)) {
      lines.shift();
    }

    const parsedRows: GridRow[] = [];

    lines.forEach((line, index) => {
      // Try tab-separated first (Excel default), then comma-separated
      const cells = line.includes('\t') 
        ? line.split('\t') 
        : line.split(',').map(cell => cell.trim());

      // Ensure we have the right number of columns
      const paddedCells = [...cells];
      while (paddedCells.length < LINE_ITEM_COLUMNS.length) {
        paddedCells.push('');
      }

      parsedRows.push({
        id: `row-${Date.now()}-${index}`,
        rowNumber: index + 1,
        cells: paddedCells.slice(0, LINE_ITEM_COLUMNS.length).map((value, colIdx) => ({ 
          value: colIdx === 9 ? value.trim().toLowerCase() : value.trim() 
        })),
      });
    });

    // Resolve market names to market_codes for pasted data
    const unresolvedPasteMarkets: string[] = [];
    if (salesMarkets && salesMarkets.length > 0) {
      for (const row of parsedRows) {
        const marketCell = row.cells[0]; // market is column 0
        if (marketCell && marketCell.value) {
          const resolved = resolveMarketCode(salesMarkets, marketCell.value);
          if (resolved) {
            marketCell.value = resolved;
          } else {
            unresolvedPasteMarkets.push(row.id);
          }
        }
      }
    }

    // Resolve media format names to media_format_ids for pasted data
    const unresolvedPasteMedia: string[] = [];
    if (mediaFormats && mediaFormats.length > 0) {
      for (const row of parsedRows) {
        const mediaTypeCell = row.cells[1]; // mediaType is column 1
        if (mediaTypeCell && mediaTypeCell.value) {
          const resolved = resolveMediaFormatId(mediaFormats, mediaTypeCell.value);
          if (resolved) {
            mediaTypeCell.value = resolved.toString();
          } else {
            unresolvedPasteMedia.push(row.id);
          }
        }
      }
    }

    // Check if all existing rows are empty (no user-entered data)
    const allExistingRowsEmpty = rows.every(row =>
      row.cells.every(cell => !cell.value.trim())
    );

    if (allExistingRowsEmpty) {
      // Clear errors for rows being replaced
      rows.forEach(row => clearRowErrors(row.id));
      // Replace empty rows with pasted data, then add a trailing empty row
      const emptyRow: GridRow = {
        id: `row-${Date.now()}-empty`,
        rowNumber: parsedRows.length + 1,
        cells: createEmptyCells(),
      };
      setRows([...parsedRows, emptyRow]);
    } else {
      // Append pasted data after existing rows, renumber accordingly
      const offset = rows.length;
      const renumbered = parsedRows.map((r, i) => ({ ...r, rowNumber: offset + i + 1 }));
      setRows(prev => [...prev, ...renumbered]);
    }
    
    // Clear the paste area
    if (pasteAreaRef.current) {
      pasteAreaRef.current.value = '';
    }

    // Set error icons for unresolved paste values
    for (const rowId of unresolvedPasteMarkets) {
      setGridError(rowId, 0, 'Invalid market');
    }
    for (const rowId of unresolvedPasteMedia) {
      setGridError(rowId, 1, 'Invalid media format');
    }
  }, [rows, clearRowErrors, setGridError, salesMarkets, mediaFormats]);

  const handleAddRow = () => {
    addEmptyRow();
  };

  const handleDeleteRow = (rowId: string) => {
    clearRowErrors(rowId);
    setRows(prev => {
      const filtered = prev.filter(row => row.id !== rowId);
      // Renumber rows
      return filtered.map((row, index) => ({
        ...row,
        rowNumber: index + 1,
      }));
    });
  };

  const handleClearAll = () => {
    // Reset to 3 empty rows
    setRows(Array.from({ length: 3 }, (_, index) => ({
      id: `row-${Date.now()}-${index}`,
      rowNumber: index + 1,
      cells: createEmptyCells(),
    })));
    setImportInfo(null);
    setIsSubmitted(false);
    // Clear localStorage draft
    localStorage.removeItem(FORM_STORAGE_KEY);
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const apiBaseUrl = env.API_BASE_URL;
      
      const response = await fetch(`${apiBaseUrl}/holds/template`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      // Get the blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'hold-request-template.xlsx';
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Failed to download template. Please try again.');
    }
  };

  // Excel file upload handlers
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (file: File) => {
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.csv')) {
      setUploadError('Please select a valid file (.xlsx, .xls, or .csv)');
      setUploadFile(null);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size must be less than 10MB');
      setUploadFile(null);
      return;
    }
    setUploadFile(file);
    setUploadError(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  };

  const handleUploadFile = async () => {
    if (!uploadFile) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const token = localStorage.getItem('auth_token');
      const apiBaseUrl = env.API_BASE_URL;

      const response = await fetch(`${apiBaseUrl}/holds/parse-template`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.data?.rows) {
        // Parse uploaded rows into grid rows
        const parsedRows: GridRow[] = result.data.rows.map((row: Record<string, string>, index: number) => ({
          id: `row-${Date.now()}-${index}`,
          rowNumber: index + 1,
          cells: LINE_ITEM_COLUMNS.map((col) => ({
            value: row[col.key] || '',
          })),
        }));

        // Resolve market names to market_codes for uploaded data
        const unresolvedMarketRows: string[] = [];
        if (salesMarkets && salesMarkets.length > 0) {
          for (const row of parsedRows) {
            const marketCell = row.cells[0]; // market is column 0
            if (marketCell && marketCell.value) {
              const resolved = resolveMarketCode(salesMarkets, marketCell.value);
              if (resolved) {
                marketCell.value = resolved;
              } else {
                unresolvedMarketRows.push(row.id);
              }
            }
          }
        }

        // Resolve media format names to media_format_ids for uploaded data
        const unresolvedMediaRows: string[] = [];
        if (mediaFormats && mediaFormats.length > 0) {
          for (const row of parsedRows) {
            const mediaTypeCell = row.cells[1]; // mediaType is column 1
            if (mediaTypeCell && mediaTypeCell.value) {
              const resolved = resolveMediaFormatId(mediaFormats, mediaTypeCell.value);
              if (resolved) {
                mediaTypeCell.value = resolved.toString();
              } else {
                unresolvedMediaRows.push(row.id);
              }
            }
          }
        }

        // Filter out empty rows — a row needs at least a market or media type to be meaningful
        const nonEmptyRows = parsedRows
          .filter(row => {
            const market = row.cells[0]?.value.trim();
            const mediaType = row.cells[1]?.value.trim();
            return market !== '' || mediaType !== '';
          })
          .map((row, index) => ({ ...row, rowNumber: index + 1 }));

        // Excel upload always replaces all existing rows
        clearAllErrors();
        setRows(() => {
          return nonEmptyRows;
        });

        // Set import info banner
        setImportInfo({
          rowCount: nonEmptyRows.length,
          format: result.data.format === 'cms' ? 'CMS Proposal Grid' : 'Excel template',
        });

        // Set error icons for unresolved market and media type values
        for (const rowId of unresolvedMarketRows) {
          setGridError(rowId, 0, 'Invalid market');
        }
        for (const rowId of unresolvedMediaRows) {
          setGridError(rowId, 1, 'Invalid media format');
        }

        // Reset upload state
        setUploadFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else if (response.status === 400) {
        const msg = result.message || `Validation failed: ${result.data?.errors?.length || 0} error(s). Fix the file and try again.`;
        setUploadError(msg);
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(err instanceof Error ? err.message : 'Failed to process file');
    } finally {
      setIsUploading(false);
    }
  };

  const detectDuplicates = (): string[] => {
      const duplicates: string[] = [];
      const seen = new Map<string, number>();

      rows.forEach((row, index) => {
        // Create a signature from key fields (market, mediaType, unitId/coverage, startDate, endDate)
        const market = row.cells[0]?.value.trim().toLowerCase();
        const mediaType = row.cells[1]?.value.trim().toLowerCase();
        const unitId = row.cells[2]?.value.trim().toLowerCase();
        const coverage = row.cells[3]?.value.trim().toLowerCase();
        const startDate = row.cells[7]?.value.trim();
        const endDate = row.cells[10]?.value.trim();

        // Skip empty rows
        if (!market && !mediaType && !unitId && !coverage && !startDate && !endDate) {
          return;
        }

        const signature = `${market}|${mediaType}|${unitId || coverage}|${startDate}|${endDate}`;

        if (seen.has(signature)) {
          const firstRowNum = seen.get(signature)! + 1;
          const currentRowNum = index + 1;
          duplicates.push(`Row ${currentRowNum} appears to be a duplicate of row ${firstRowNum}`);
        } else {
          seen.set(signature, index);
        }
      });

      return duplicates;
    };

  const handleSubmit = async () => {
    // Filter out fully empty rows before validation
    const nonEmptyRows = rows.filter(row => 
      row.cells.some(cell => cell.value.trim() !== '')
    );
    
    // Validate header hold expiration date (max 10 business days)
    // Compare as YYYY-MM-DD strings — avoids new Date(M/d/yyyy) UTC shift in UTC+ timezones
    const trimmedHoldExpirationDate = holdExpirationDate.trim();
    const expYMD = parseDateToLocalYMD(trimmedHoldExpirationDate);
    if (trimmedHoldExpirationDate && !expYMD) {
      setHoldExpirationDateError('Enter a valid hold expiration date');
      return;
    }
    if (expYMD && expYMD > maxHoldExpirationDate) {
      setHoldExpirationDateError('Hold expiration date cannot exceed 10 business days');
      return;
    }
    
    // Build set of valid media format IDs for submit-time validation
    // Only pass when mediaFormats is loaded — undefined skips existence check
    const validMediaFormatIds = mediaFormats && mediaFormats.length > 0
      ? new Set<number>(mediaFormats.map(f => f.id))
      : undefined;

    // Validate all fields using the shared validation hook (only non-empty rows)
    if (!validateAll({ advertiser, businessCategory }, nonEmptyRows, validMediaFormatIds, holdExpirationDate)) {
      // Scroll to first invalid field
      const firstInvalid = document.querySelector('[aria-invalid="true"]');
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Convert grid data to line items format (already filtered)
    const lineItems = nonEmptyRows.map(row => {
        const rowData: Record<string, string | number | boolean | undefined> = {};
        LINE_ITEM_COLUMNS.forEach((col, index) => {
          rowData[col.key] = row.cells[index]?.value || '';
        });
        // Convert isException to isExceptionHold boolean
        rowData.isExceptionHold = rowData.isException === 'true';
        delete rowData.isException;
        // Convert mediaType (stored as id string) to media_format_id integer
        const mediaFormatIdStr = String(rowData.mediaType || '');
        const parsedId = parseInt(mediaFormatIdStr, 10);
        if (!isNaN(parsedId) && parsedId > 0) {
          rowData.media_format_id = parsedId;
        }
        delete rowData.mediaType;
        // Resolve endDate vs period conflict: backend rejects rows with both endDate
        // AND (typeOfPeriod + noOfPeriods).
        // When user explicitly set period fields, clear endDate (backend calculates it).
        // When user manually edited endDate after auto-calc, clear period fields (endDate wins).
        const userSetPeriods = row.cells[8]?.value.trim() && row.cells[9]?.value.trim();
        if (rowData.endDate && userSetPeriods) {
          // Both present — clear period fields and keep the displayed endDate.
          // The endDate shown in the grid is what the user sees and expects to be submitted.
          delete rowData.noOfPeriods;
          delete rowData.typeOfPeriod;
        }
        // Clear default holdExpirationDate so backend calculates it at submission time
        // Use parseDateToLocalYMD for timezone-safe comparison (avoids new Date() UTC shift)
        if (rowData.holdExpirationDate) {
          const rowYMD = parseDateToLocalYMD(String(rowData.holdExpirationDate));
          const defaultYMD = parseDateToLocalYMD(defaultHoldExpirationDate);
          if (rowYMD && defaultYMD && rowYMD === defaultYMD) {
            delete rowData.holdExpirationDate;
          }
        }
        return rowData;
      });

    if (lineItems.length === 0) {
      alert('Please add at least one line item');
      return;
    }

    // Check for duplicates
    const duplicates = detectDuplicates();
    if (duplicates.length > 0) {
      const proceed = window.confirm(
        `Possible duplicate rows detected:\n\n${duplicates.join('\n')}\n\nDo you want to proceed anyway?`
      );
      if (!proceed) {
        return;
      }
    }

    // Auto-detect cross-region and Service AE
    try {
      const token = localStorage.getItem('auth_token');
      const apiBaseUrl = env.API_BASE_URL;
      
      const markets = [...new Set(lineItems.map(item => String(item.market || '')).filter(Boolean))];
      const serviceAeEmails = [...new Set(lineItems.map(item => String(item.serviceAeEmail || '')).filter(Boolean))];
      
      let detectedCrossRegion = isCrossRegion;
      let serviceAeId: string | undefined = undefined;

      // Check for cross-region
      if (markets.length > 0) {
        const response = await fetch(
          `${apiBaseUrl}/product-catalog/regions?markets=${markets.join(',')}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          detectedCrossRegion = result.data?.isCrossRegion || isCrossRegion;
        }
      }

      // Look up Service AE by email if provided
      if (serviceAeEmails.length > 0) {
        const serviceAeEmail = serviceAeEmails[0]!;
        
        try {
          const response = await fetch(
            `${apiBaseUrl}/users/search?search=${encodeURIComponent(serviceAeEmail)}&limit=10`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.ok) {
            const result = await response.json();
            const matchedUser = result.data?.users?.find((u: { email?: string; id?: string }) => 
              u.email && u.email.toLowerCase() === serviceAeEmail.toLowerCase()
            );
            
            if (matchedUser) {
              serviceAeId = matchedUser.id;
              detectedCrossRegion = true;
            }
          }
        } catch (err) {
          console.error('Error looking up Service AE:', err);
        }
      }

      // Submit the data
      await onSubmit({
        holdRequest: {
          advertiser,
          businessCategory,
          isCrossRegion: detectedCrossRegion,
          serviceAeId,
          // Send today's date in the user's local timezone to avoid server UTC mismatch
          holdStartDate: getLocalTodayYMD(),
          // Only send holdExpirationDate when the user explicitly changed it from the default.
          // serializeHoldExpirationDate normalises both values to YYYY-MM-DD before comparing
          // so the check is timezone-safe (avoids toDateString() UTC vs local mismatch).
          holdExpirationDate: serializeHoldExpirationDate(holdExpirationDate, defaultHoldExpirationDate),
        },
        lineItems,
      });
      
      // On successful submission (no error thrown), disable button and clear the form
      setIsSubmitted(true);
      setAdvertiser('');
      setBusinessCategoryInput('');
      setBusinessCategory('');
      setIsCrossRegion(false);
      setHoldExpirationDate(defaultHoldExpirationDate);
      // Reset to 3 empty rows
      setRows(Array.from({ length: 3 }, (_, index) => ({
        id: `row-${Date.now()}-${index}`,
        rowNumber: index + 1,
        cells: createEmptyCells(),
      })));
      setImportInfo(null);
      clearAllErrors();
      // Clear localStorage draft
      localStorage.removeItem(FORM_STORAGE_KEY);
    } catch (err) {
      // Don't clear form on error - user needs to fix and retry
      console.error('Error processing data:', err);
      // Form data is preserved - user can fix errors and resubmit
      // The uploadErrors prop will be populated by the parent component
    }
  };

  return (
    <div className="space-y-6">
      {/* Contract Information Section */}
      <Card>
        <CardHeader>
          <CardTitle>Contract Information</CardTitle>
          <CardDescription>
            Provide high-level details about this hold request.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="advertiser" className="block text-sm font-medium text-neutral-600 mb-1">
                Advertiser Name <span className="text-red-500">*</span>
              </label>
              <ErrorIndicator error={headerErrors.advertiser} fieldId="advertiser">
                <Input
                  id="advertiser"
                  type="text"
                  value={advertiser}
                  onChange={(e) => {
                    setAdvertiser(e.target.value);
                    if (headerErrors.advertiser) {
                      validateHeaderField('advertiser', e.target.value);
                    }
                  }}
                  onBlur={() => validateHeaderField('advertiser', advertiser)}
                  placeholder="Enter advertiser name"
                  disabled={isProcessing}
                />
              </ErrorIndicator>
            </div>

            <div className="relative">
              <label htmlFor="businessCategory" className="block text-sm font-medium text-neutral-600 mb-1">
                Business Category <span className="text-red-500">*</span>
              </label>
              <ErrorIndicator error={headerErrors.businessCategory} fieldId="businessCategory">
                <input
                  id="businessCategory"
                  type="text"
                  value={businessCategoryInput}
                  onChange={(e) => {
                    setBusinessCategoryInput(e.target.value);
                    setBusinessCategory(e.target.value);
                    setShowBusinessCategorySuggestions(true);
                    if (headerErrors.businessCategory) {
                      validateHeaderField('businessCategory', e.target.value);
                    }
                  }}
                  onFocus={() => setShowBusinessCategorySuggestions(true)}
                  onBlur={() => {
                    setTimeout(() => setShowBusinessCategorySuggestions(false), 200);
                    validateHeaderField('businessCategory', businessCategoryInput);
                  }}
                  placeholder="Type to search categories..."
                  disabled={isProcessing}
                  className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
                />
              </ErrorIndicator>
              
              {showBusinessCategorySuggestions && businessCategorySuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-md  max-h-60 overflow-auto">
                  {businessCategorySuggestions.map((category) => (
                    <button
                      key={category}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-neutral-100 text-sm"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setBusinessCategoryInput(category);
                        setBusinessCategory(category);
                        setShowBusinessCategorySuggestions(false);
                      }}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isCrossRegion"
                checked={isCrossRegion}
                onChange={(e) => setIsCrossRegion(e.target.checked)}
                disabled={isProcessing}
                className="h-4 w-4 rounded border-neutral-200 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="isCrossRegion" className="text-sm text-neutral-600">
                This is a cross-region request
              </label>
            </div>

            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-neutral-600 mb-1">
                  Hold Request Date
                </label>
                <Input
                  type="date"
                  value={holdRequestDate}
                  disabled
                  className="bg-neutral-50"
                />
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-neutral-600 mb-1">
                  Hold Expiration Date
                </label>
                <DatePicker
                  value={holdExpirationDate}
                  onChange={(val) => {
                    setHoldExpirationDate(val);
                    // Validate against 10 business day max — compare as YYYY-MM-DD strings
                    // to avoid new Date(M/d/yyyy) UTC shift in UTC+ timezones
                    const parsedYMD = parseDateToLocalYMD(val);
                    if (parsedYMD) {
                      if (parsedYMD > maxHoldExpirationDate) {
                        setHoldExpirationDateError('Hold expiration date cannot exceed 10 business days');
                      } else {
                        setHoldExpirationDateError(null);
                      }
                    }
                    // Prepopulate all line item rows with the new header date
                    const holdExpColIndex = LINE_ITEM_COLUMNS.findIndex(c => c.key === 'holdExpirationDate');
                    if (holdExpColIndex >= 0) {
                      setRows(prev => prev.map(row => {
                        // Only update if the row doesn't have exception checked
                        const exceptionColIndex = LINE_ITEM_COLUMNS.findIndex(c => c.key === 'isException');
                        const isExceptionChecked = row.cells[exceptionColIndex]?.value === 'true';
                        if (!isExceptionChecked) {
                          const newCells = [...row.cells];
                          newCells[holdExpColIndex] = { value: val };
                          return { ...row, cells: newCells };
                        }
                        return row;
                      }));
                    }
                  }}
                  disabled={isProcessing}
                  minDate={startOfDay(new Date())}
                  error={holdExpirationDateError || undefined}
                />
                <p className="text-xs text-neutral-400 mt-1">
                  Max 10 business days (default: 5 business days)
                </p>
              </div>
            </div>
          </div>

          {requiresMoviesGap(businessCategory) && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                <strong>Movies Requirements:</strong> A minimum 60 business-day gap is required 
                between the hold expiration date and flight start date for all line items.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Line Items Section */}
      <Card>
        <CardHeader>
          <CardTitle>Add Line Items</CardTitle>
          <CardDescription>
            Add line items using any method below. All methods populate the table for review before submitting.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Tab Selector */}
          <div className="flex border-b border-neutral-200">
            {([
              { key: 'upload' as LineItemInputTab, label: 'Upload Excel' },
              // { key: 'paste' as LineItemInputTab, label: 'Paste Data' },
              { key: 'manual' as LineItemInputTab, label: 'Enter Manually' },
            ]).map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-neutral-400 hover:text-neutral-600 hover:border-neutral-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Upload Excel Tab */}
          {activeTab === 'upload' && (
            <div className="space-y-3">
              <p className="text-sm text-neutral-600">
                Drag and drop a file or{' '}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-primary-600 hover:text-primary-700 underline bg-transparent border-none p-0 font-inherit cursor-pointer"
                >
                  browse
                </button>
                {' '}to upload. We'll add the rows to the table below.
              </p>

              {/* Drop Zone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`flex flex-col items-center justify-center py-10 border-2 border-dashed rounded-lg transition-colors ${
                  isDragOver
                    ? 'border-primary-400 bg-primary-50'
                    : 'border-neutral-200 bg-neutral-50 hover:border-neutral-400'
                }`}
              >
                <svg className="w-10 h-10 text-primary-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <span>Drag & drop or</span>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isProcessing || isUploading}
                    className="text-sm"
                  >
                    Browse file
                  </Button>
                </div>
                <p className="text-xs text-neutral-400 mt-2">
                  .xlsx or .csv file{' '}
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="text-primary-600 hover:text-primary-700 underline bg-transparent border-none p-0 font-inherit cursor-pointer"
                  >
                    (Download template)
                  </button>
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Selected file info */}
              {uploadFile && (
                <div className="flex items-center justify-between p-3 bg-neutral-50 border border-neutral-200 rounded-md">
                  <span className="text-sm text-neutral-600">{uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)</span>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setUploadFile(null); setUploadError(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      disabled={isUploading}
                    >
                      Remove
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleUploadFile}
                      disabled={isUploading}
                      isLoading={isUploading}
                    >
                      {isUploading ? 'Processing...' : 'Upload'}
                    </Button>
                  </div>
                </div>
              )}

              {uploadError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">{uploadError}</p>
                </div>
              )}

              <button
                type="button"
                onClick={() => setShowFormattingRules(true)}
                className="text-sm text-primary-600 hover:text-primary-700 underline bg-transparent border-none p-0 font-inherit cursor-pointer"
              >
                View formatting rules
              </button>
            </div>
          )}

          {/* Paste Data Tab - temporarily disabled */}
          {/* {activeTab === 'paste' && (
            <div className="space-y-3">
              <p className="text-sm text-neutral-600">
                Copy rows from Excel or Google Sheets and paste below. Data will populate the review table.
              </p>
              <textarea
                ref={pasteAreaRef}
                onPaste={_handlePaste}
                placeholder="Click here and paste your spreadsheet data (Ctrl+V or Cmd+V)..."
                className="w-full h-32 px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                disabled={isProcessing}
              />
              <button
                type="button"
                onClick={() => setShowFormattingRules(true)}
                className="text-sm text-primary-600 hover:text-primary-700 underline bg-transparent border-none p-0 font-inherit cursor-pointer"
              >
                View formatting rules
              </button>
            </div>
          )} */}

          {/* Enter Manually Tab */}
          {activeTab === 'manual' && (
            <div className="space-y-3">
              <p className="text-sm text-neutral-600">
                Add rows directly in the review table below using the "Add row" button.
              </p>
              <button
                type="button"
                onClick={() => setShowFormattingRules(true)}
                className="text-sm text-primary-600 hover:text-primary-700 underline bg-transparent border-none p-0 font-inherit cursor-pointer"
              >
                View formatting rules
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Formatting Rules Modal */}
      <Modal isOpen={showFormattingRules} onClose={() => setShowFormattingRules(false)} size="lg">
        <div className="bg-white rounded-lg  p-6 space-y-4">
          <h3 className="text-lg font-semibold text-neutral-900">Formatting Rules</h3>
          <div className="space-y-3 text-sm text-neutral-600">
            <div>
              <p className="font-medium text-neutral-900 mb-1">Unit ID / Product ID / Coverage</p>
              <p>
                Either "Unit ID / Product ID" must contain a valid Product ID from the product catalog,
                OR the "Coverage" field must contain coverage details. At least one of these fields is
                required per row.
              </p>
            </div>
            <div>
              <p className="font-medium text-neutral-900 mb-1">Period Fields</p>
              <p className="mb-1">Users must select one of the following combinations:</p>
              <ul className="list-disc list-inside ml-2 space-y-0.5">
                <li>Start Date + No of Periods + Type of Period</li>
                <li>Start Date + End Date</li>
              </ul>
              <p className="mt-1 text-neutral-600">Using Start Date, End Date, and Period together is not allowed.</p>
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button type="button" variant="primary" onClick={() => setShowFormattingRules(false)}>
              OK
            </Button>
          </div>
        </div>
      </Modal>

      {/* Review Line Items Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Review line items</CardTitle>
              <CardDescription>
                Rows will appear in the table below for review before you submit.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={handleAddRow} disabled={isProcessing}>
                Add row
              </Button>
              <Button type="button" variant="outline" onClick={handleClearAll} disabled={isProcessing}>
                Clear all
              </Button>
              {rows.length > 0 && (
                <button
                  type="button"
                  onClick={() => setIsGridExpanded(true)}
                  className="text-neutral-400 hover:text-neutral-600 p-1 rounded hover:bg-neutral-100"
                  title="Expand grid to full screen"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* Import Info Banner */}
          {importInfo && (
            <div className="flex items-center justify-between p-3 bg-outfront-purple-light border border-outfront-purple rounded-md">
              <p className="text-sm text-outfront-purple">
                Imported {importInfo.rowCount} row{importInfo.rowCount !== 1 ? 's' : ''} from {importInfo.format}. Review and edit below before submitting.
              </p>
              <button
                type="button"
                onClick={() => setImportInfo(null)}
                className="text-outfront-purple hover:text-outfront-purple flex-shrink-0 ml-3"
                title="Dismiss"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Data Grid */}
          {rows.length > 0 && (
            <div className="border border-neutral-200 rounded-md overflow-visible">
              <div className="overflow-x-auto min-h-[480px] max-h-[520px] overflow-y-auto">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50 sticky top-0 z-20">
                    <tr>
                      <th className="sticky left-0 z-10 bg-neutral-50 px-3 py-2 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider w-12">
                        #
                      </th>
                      {LINE_ITEM_COLUMNS.map((col, index) => (
                        <th
                          key={index}
                          className="px-3 py-2 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider min-w-[150px]"
                        >
                          <span className="flex items-center gap-1">
                            {col.label}
                            {col.required && <span className="text-red-500">*</span>}
                            {col.key === 'unitId' && (
                              <span className="relative group">
                                <svg className="h-5 w-5 cursor-help text-neutral-400" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                                </svg>
                                <span className="absolute left-0 top-full mt-1 w-56 px-2 py-1.5 text-xs font-normal normal-case tracking-normal text-white bg-neutral-700 rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[9999]">
                                  Please ensure Unit IDs match the Inventory Number in IMS and CMS. Static inventory must include suffix.
                                </span>
                              </span>
                            )}
                          </span>
                        </th>
                      ))}
                      <th className="sticky right-0 z-10 bg-neutral-50 px-3 py-2 text-center text-xs font-medium text-neutral-400 uppercase tracking-wider w-20">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200 [&>tr:last-child>td]:pb-8">
                    {rows.map((row) => (
                      <tr key={row.id} className="hover:bg-neutral-50">
                        <td className="sticky left-0 z-10 bg-white px-3 py-2 text-sm text-neutral-400 font-medium">
                          {row.rowNumber}
                        </td>
                        {row.cells.map((cell, colIndex) => {
                          const column = LINE_ITEM_COLUMNS[colIndex];
                          const isDateColumn = column?.key === 'startDate' || column?.key === 'endDate';
                          const isHoldExpirationDate = column?.key === 'holdExpirationDate';
                          const isShowingType = column?.key === 'showingType';
                          const isException = column?.key === 'isException';
                          const isTypeOfPeriod = column?.key === 'typeOfPeriod';
                          const isNoOfPeriods = column?.key === 'noOfPeriods';
                          const isMarket = column?.key === 'market';
                          const isMediaType = column?.key === 'mediaType';
                          const isUnitId = column?.key === 'unitId';

                          // Determine if rate should be disabled (zero-amount showing type selected)
                          const isNegotiatedRate = column?.key === 'negotiatedPerPeriodRate';
                          const currentShowingType = row.cells[SHOWING_TYPE_COL_INDEX]?.value || '';
                          const isRateDisabled = isNegotiatedRate && isZeroAmountShowingType(currentShowingType);
                          
                          // Grid-level Unit ID validation state for this row
                          const isActiveValidationRow = isUnitId && gridValidationRowId === row.id;
                          const showGridValidationLoading = isActiveValidationRow && (gridIsValidating || gridIsFetching);
                          const showGridValidationSuccess = isActiveValidationRow && gridValidationResult?.valid;
                          const showGridValidationError = isActiveValidationRow && gridValidationResult && !gridValidationResult.valid && !gridValidationResult.needsDisambiguation;
                          const showGridDisambiguation = isActiveValidationRow && gridValidationResult?.needsDisambiguation;
                          
                          // Get exception status for this row
                          const exceptionColIndex = LINE_ITEM_COLUMNS.findIndex(c => c.key === 'isException');
                          const isExceptionChecked = row.cells[exceptionColIndex]?.value === 'true';
                          
                          return (
                          <td
                            key={colIndex}
                            className="px-1 py-1"
                          >
                            <ErrorIndicator error={getGridError(row.id, colIndex)} fieldId={`grid-${row.id}-${colIndex}`}>
                            {isException ? (
                              <div className="flex justify-center">
                                <input
                                  type="checkbox"
                                  checked={cell.value === 'true'}
                                  onChange={(e) => {
                                    const newValue = e.target.checked ? 'true' : 'false';
                                    handleCellChange(row.id, colIndex, newValue);
                                    
                                    // Show modal on first exception check in session
                                    if (e.target.checked && !hasShownExceptionModal.current) {
                                      hasShownExceptionModal.current = true;
                                      sessionStorage.setItem('exceptionModalShown', 'true');
                                      setShowExceptionModal(true);
                                    }
                                    
                                    // Reset hold expiration date to default when unchecking
                                    if (!e.target.checked) {
                                      const holdExpColIndex = LINE_ITEM_COLUMNS.findIndex(c => c.key === 'holdExpirationDate');
                                      if (holdExpColIndex >= 0) {
                                        handleCellChange(row.id, holdExpColIndex, holdExpirationDate);
                                      }
                                    }
                                  }}
                                  disabled={isProcessing}
                                  className="h-4 w-4 rounded border-neutral-200 text-primary-600 focus:ring-primary-500"
                                />
                              </div>
                            ) : isHoldExpirationDate ? (
                              <DatePicker
                                value={cell.value || holdExpirationDate}
                                onChange={(val) => handleCellChange(row.id, colIndex, val)}
                                onBlur={() => validateGridCell(row.id, colIndex, cell.value, row, holdExpirationDate)}
                                disabled={isProcessing || !isExceptionChecked}
                                minDate={startOfDay(new Date())}
                                compact
                              />
                            ) : isDateColumn ? (
                              <DatePicker
                                value={cell.value}
                                onChange={(val) => handleCellChange(row.id, colIndex, val)}
                                onBlur={() => validateGridCell(row.id, colIndex, cell.value, row, holdExpirationDate)}
                                disabled={isProcessing}
                                compact
                                defaultToToday
                              />
                            ) : isNoOfPeriods ? (
                              <input
                                type="text"
                                value={cell.value}
                                placeholder="1"
                                onChange={(e) => handleCellChange(row.id, colIndex, e.target.value)}
                                onBlur={() => validateGridCell(row.id, colIndex, cell.value, row, holdExpirationDate)}
                                className="w-full px-2 py-1 text-sm border border-neutral-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 placeholder:text-neutral-300"
                                disabled={isProcessing}
                              />
                            ) : isTypeOfPeriod ? (
                              <select
                                value={cell.value || '4wks'}
                                onChange={(e) => handleCellChange(row.id, colIndex, e.target.value)}
                                onBlur={() => validateGridCell(row.id, colIndex, cell.value || '4wks', row, holdExpirationDate)}
                                className="w-full px-2 py-1 text-sm border border-neutral-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                disabled={isProcessing}
                              >
                                <option value="4wks">4wks</option>
                                <option value="days">days</option>
                                <option value="weeks">weeks</option>
                                <option value="months">months</option>
                              </select>
                            ) : isMarket ? (
                              <div>
                                <MarketTypeahead
                                  value={cell.value}
                                  onChange={(code) => handleCellChange(row.id, colIndex, code)}
                                  markets={salesMarkets ?? []}
                                  isLoading={marketsLoading}
                                  error={getGridError(row.id, colIndex)}
                                  disabled={isProcessing}
                                  onBlur={() => validateGridCell(row.id, colIndex, cell.value, row, holdExpirationDate)}
                                />
                              </div>
                            ) : isMediaType ? (
                              <div>
                                <MediaFormatTypeahead
                                  value={cell.value}
                                  onChange={(id) => handleCellChange(row.id, colIndex, id)}
                                  mediaFormats={mediaFormats ?? []}
                                  isLoading={mediaFormatsLoading}
                                  error={getGridError(row.id, colIndex)}
                                  disabled={isProcessing}
                                  onBlur={() => validateGridCell(row.id, colIndex, cell.value, row, holdExpirationDate)}
                                />
                              </div>
                            ) : isUnitId ? (
                            <div className="relative">
                              <input
                                type="text"
                                value={cell.value}
                                onChange={(e) => handleCellChange(row.id, colIndex, e.target.value)}
                                onBlur={(e) => {
                                  validateGridCell(row.id, colIndex, cell.value, row, holdExpirationDate);
                                  handleGridUnitIdBlur(row.id, e.target.value, row.cells[0]?.value || '');
                                }}
                                className="w-full px-2 py-1 text-sm border border-neutral-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                disabled={isProcessing}
                              />
                              {showGridValidationLoading && (
                                <div className="mt-1 flex items-center gap-1 text-xs text-neutral-400">
                                  <LoadingSpinner size="sm" />
                                  <span>Validating...</span>
                                </div>
                              )}
                              {showGridValidationSuccess && null}
                              {showGridDisambiguation && gridValidationResult?.markets && (
                                <div className="absolute left-0 top-full mt-1 z-[9999] bg-white border border-neutral-200 rounded-md shadow-lg min-w-[200px]">
                                  <p className="px-3 py-1.5 text-xs text-neutral-500 border-b border-neutral-100">
                                    Found in {gridValidationResult.markets.length} markets:
                                  </p>
                                  <ul className="max-h-[200px] overflow-y-auto">
                                    {gridValidationResult.markets.map((mkt) => (
                                      <li
                                        key={mkt}
                                        className="px-3 py-2 text-sm cursor-pointer hover:bg-[#EEE9F9]"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          handleGridDisambiguationSelect(row.id, mkt);
                                        }}
                                      >
                                        {mkt}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {showGridValidationError && gridValidationResult?.error && null}
                            </div>
                            ) : isShowingType ? (
                              <select
                                value={cell.value || ''}
                                onChange={(e) => handleCellChange(row.id, colIndex, e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-neutral-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                disabled={isProcessing || isLoadingShowingTypes || showingTypes.length === 0}
                              >
                                <option value="">{isLoadingShowingTypes ? 'Loading...' : showingTypes.length === 0 ? 'None available' : 'Select...'}</option>
                                {showingTypes.map((st) => (
                                  <option key={st.code} value={st.code}>{st.label}</option>
                                ))}
                              </select>
                            ) : (
                            <input
                              type="text"
                              value={isRateDisabled ? '0' : cell.value}
                              onChange={(e) => handleCellChange(row.id, colIndex, e.target.value)}
                              onBlur={() => validateGridCell(row.id, colIndex, cell.value, row, holdExpirationDate)}
                              className={`w-full px-2 py-1 text-sm border border-neutral-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 ${isRateDisabled ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed' : ''}`}
                              disabled={isProcessing || isRateDisabled}
                            />
                            )}
                            </ErrorIndicator>
                          </td>
                          );
                        })}
                        <td className="sticky right-0 z-10 bg-white px-3 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => handleDeleteRow(row.id)}
                            disabled={isProcessing}
                            className="text-red-600 hover:text-red-800 disabled:opacity-50"
                            title="Delete row"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

      {/* Expanded Grid Overlay */}
      {isGridExpanded && rows.length > 0 && (
        <div className="fixed inset-0 z-50 bg-neutral-50 flex flex-col  border-t-4 border-primary-600">
          {/* Overlay Header */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-neutral-200 bg-neutral-100  flex-shrink-0">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-semibold text-neutral-900">Line Items</h3>
              <span className="text-sm text-neutral-400">
                {rows.length} row{rows.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => { setIsGridExpanded(false); setActiveTab('paste'); }} disabled={isProcessing}>
                Paste More Data
              </Button>
              <Button type="button" variant="outline" onClick={handleAddRow} disabled={isProcessing}>
                Add Row
              </Button>
              <Button type="button" variant="outline" onClick={handleClearAll} disabled={isProcessing}>
                Clear All
              </Button>
              <button
                type="button"
                onClick={() => setIsGridExpanded(false)}
                className="ml-2 text-neutral-400 hover:text-neutral-600 p-1.5 rounded hover:bg-neutral-200"
                title="Collapse grid (Esc)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                </svg>
              </button>
            </div>
          </div>

          {/* Overlay Grid */}
          <div className="flex-1 overflow-auto px-6 py-4">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50 sticky top-0 z-20">
                <tr>
                  <th className="sticky left-0 z-30 bg-neutral-50 px-3 py-2 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider w-12">
                    #
                  </th>
                  {LINE_ITEM_COLUMNS.map((col, index) => (
                    <th
                      key={index}
                      className="px-3 py-2 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider min-w-[150px]"
                    >
                      <span className="flex items-center gap-1">
                        {col.label}
                        {col.required && <span className="text-red-500">*</span>}
                        {col.key === 'unitId' && (
                          <span className="relative group">
                            <svg className="h-5 w-5 cursor-help text-neutral-400" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                            </svg>
                            <span className="absolute left-0 top-full mt-1 w-56 px-2 py-1.5 text-xs font-normal normal-case tracking-normal text-white bg-neutral-700 rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[9999]">
                              Please ensure Unit IDs match the Inventory Number in IMS and CMS. Static inventory must include suffix.
                            </span>
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                  <th className="sticky right-0 z-30 bg-neutral-50 px-3 py-2 text-center text-xs font-medium text-neutral-400 uppercase tracking-wider w-20">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-neutral-50">
                    <td className="sticky left-0 z-10 bg-white px-3 py-2 text-sm text-neutral-400 font-medium">
                      {row.rowNumber}
                    </td>
                    {row.cells.map((cell, colIndex) => {
                      const column = LINE_ITEM_COLUMNS[colIndex];
                      const isDateColumn = column?.key === 'startDate' || column?.key === 'endDate';
                      const isHoldExpirationDate = column?.key === 'holdExpirationDate';
                      const isShowingType = column?.key === 'showingType';
                      const isException = column?.key === 'isException';
                      const isTypeOfPeriod = column?.key === 'typeOfPeriod';
                      const isNoOfPeriods = column?.key === 'noOfPeriods';
                      const isMarket = column?.key === 'market';
                      const isMediaType = column?.key === 'mediaType';
                      const isUnitId = column?.key === 'unitId';
                      const exceptionColIndex = LINE_ITEM_COLUMNS.findIndex(c => c.key === 'isException');
                      const isExceptionChecked = row.cells[exceptionColIndex]?.value === 'true';

                      // Determine if rate should be disabled (zero-amount showing type selected)
                      const isNegotiatedRate = column?.key === 'negotiatedPerPeriodRate';
                      const currentShowingType = row.cells[SHOWING_TYPE_COL_INDEX]?.value || '';
                      const isRateDisabled = isNegotiatedRate && isZeroAmountShowingType(currentShowingType);
                      
                      // Grid-level Unit ID validation state for this row (expanded overlay)
                      const isActiveValidationRow = isUnitId && gridValidationRowId === row.id;
                      const showGridValidationLoading = isActiveValidationRow && (gridIsValidating || gridIsFetching);
                      const showGridValidationSuccess = isActiveValidationRow && gridValidationResult?.valid;
                      const showGridValidationError = isActiveValidationRow && gridValidationResult && !gridValidationResult.valid && !gridValidationResult.needsDisambiguation;
                      const showGridDisambiguation = isActiveValidationRow && gridValidationResult?.needsDisambiguation;
                      
                      return (
                      <td key={colIndex} className="px-1 py-1">
                        <ErrorIndicator error={getGridError(row.id, colIndex)} fieldId={`grid-exp-${row.id}-${colIndex}`}>
                        {isException ? (
                          <div className="flex justify-center">
                            <input
                              type="checkbox"
                              checked={cell.value === 'true'}
                              onChange={(e) => {
                                const newValue = e.target.checked ? 'true' : 'false';
                                handleCellChange(row.id, colIndex, newValue);
                                
                                // Show modal on first exception check in session
                                if (e.target.checked && !hasShownExceptionModal.current) {
                                  hasShownExceptionModal.current = true;
                                  sessionStorage.setItem('exceptionModalShown', 'true');
                                  setShowExceptionModal(true);
                                }
                                
                                // Reset hold expiration date to default when unchecking
                                if (!e.target.checked) {
                                  const holdExpColIndex = LINE_ITEM_COLUMNS.findIndex(c => c.key === 'holdExpirationDate');
                                  if (holdExpColIndex >= 0) {
                                    handleCellChange(row.id, holdExpColIndex, holdExpirationDate);
                                  }
                                }
                              }}
                              disabled={isProcessing}
                              className="h-4 w-4 rounded border-neutral-200 text-primary-600 focus:ring-primary-500"
                            />
                          </div>
                        ) : isHoldExpirationDate ? (
                          <DatePicker
                            value={cell.value || holdExpirationDate}
                            onChange={(val) => handleCellChange(row.id, colIndex, val)}
                            onBlur={() => validateGridCell(row.id, colIndex, cell.value, row, holdExpirationDate)}
                            disabled={isProcessing || !isExceptionChecked}
                            minDate={startOfDay(new Date())}
                            compact
                          />
                        ) : isDateColumn ? (
                          <DatePicker
                            value={cell.value}
                            onChange={(val) => handleCellChange(row.id, colIndex, val)}
                            onBlur={() => validateGridCell(row.id, colIndex, cell.value, row, holdExpirationDate)}
                            disabled={isProcessing}
                            compact
                            defaultToToday
                          />
                        ) : isNoOfPeriods ? (
                          <input
                            type="text"
                            value={cell.value}
                            placeholder="1"
                            onChange={(e) => handleCellChange(row.id, colIndex, e.target.value)}
                            onBlur={() => validateGridCell(row.id, colIndex, cell.value, row, holdExpirationDate)}
                            className="w-full px-2 py-1 text-sm border border-neutral-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 placeholder:text-neutral-300"
                            disabled={isProcessing}
                          />
                        ) : isTypeOfPeriod ? (
                          <select
                            value={cell.value || '4wks'}
                            onChange={(e) => handleCellChange(row.id, colIndex, e.target.value)}
                            onBlur={() => validateGridCell(row.id, colIndex, cell.value || '4wks', row, holdExpirationDate)}
                            className="w-full px-2 py-1 text-sm border border-neutral-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                            disabled={isProcessing}
                          >
                            <option value="4wks">4wks</option>
                            <option value="days">days</option>
                            <option value="weeks">weeks</option>
                            <option value="months">months</option>
                          </select>
                        ) : isMarket ? (
                          <div>
                            <MarketTypeahead
                              value={cell.value}
                              onChange={(code) => handleCellChange(row.id, colIndex, code)}
                              markets={salesMarkets ?? []}
                              isLoading={marketsLoading}
                              error={getGridError(row.id, colIndex)}
                              disabled={isProcessing}
                              onBlur={() => validateGridCell(row.id, colIndex, cell.value, row, holdExpirationDate)}
                            />
                          </div>
                        ) : isMediaType ? (
                          <div>
                            <MediaFormatTypeahead
                              value={cell.value}
                              onChange={(id) => handleCellChange(row.id, colIndex, id)}
                              mediaFormats={mediaFormats ?? []}
                              isLoading={mediaFormatsLoading}
                              error={getGridError(row.id, colIndex)}
                              disabled={isProcessing}
                              onBlur={() => validateGridCell(row.id, colIndex, cell.value, row, holdExpirationDate)}
                            />
                          </div>
                        ) : isUnitId ? (
                        <div className="relative">
                          <input
                            type="text"
                            value={cell.value}
                            onChange={(e) => handleCellChange(row.id, colIndex, e.target.value)}
                            onBlur={(e) => {
                              validateGridCell(row.id, colIndex, cell.value, row, holdExpirationDate);
                              handleGridUnitIdBlur(row.id, e.target.value, row.cells[0]?.value || '');
                            }}
                            className="w-full px-2 py-1 text-sm border border-neutral-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                            disabled={isProcessing}
                          />
                          {showGridValidationLoading && (
                            <div className="mt-1 flex items-center gap-1 text-xs text-neutral-400">
                              <LoadingSpinner size="sm" />
                              <span>Validating...</span>
                            </div>
                          )}
                          {showGridValidationSuccess && null}
                          {showGridDisambiguation && gridValidationResult?.markets && (
                            <div className="absolute left-0 top-full mt-1 z-[9999] bg-white border border-neutral-200 rounded-md shadow-lg min-w-[200px]">
                              <p className="px-3 py-1.5 text-xs text-neutral-500 border-b border-neutral-100">
                                Found in {gridValidationResult.markets.length} markets:
                              </p>
                              <ul className="max-h-[200px] overflow-y-auto">
                                {gridValidationResult.markets.map((mkt) => (
                                  <li
                                    key={mkt}
                                    className="px-3 py-2 text-sm cursor-pointer hover:bg-[#EEE9F9]"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      handleGridDisambiguationSelect(row.id, mkt);
                                    }}
                                  >
                                    {mkt}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {showGridValidationError && gridValidationResult?.error && null}
                        </div>
                        ) : isShowingType ? (
                          <select
                            value={cell.value || ''}
                            onChange={(e) => handleCellChange(row.id, colIndex, e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-neutral-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                            disabled={isProcessing || isLoadingShowingTypes || showingTypes.length === 0}
                          >
                            <option value="">{isLoadingShowingTypes ? 'Loading...' : showingTypes.length === 0 ? 'None available' : 'Select...'}</option>
                            {showingTypes.map((st) => (
                              <option key={st.code} value={st.code}>{st.label}</option>
                            ))}
                          </select>
                        ) : (
                        <input
                          type="text"
                          value={isRateDisabled ? '0' : cell.value}
                          onChange={(e) => handleCellChange(row.id, colIndex, e.target.value)}
                          onBlur={() => validateGridCell(row.id, colIndex, cell.value, row, holdExpirationDate)}
                          className={`w-full px-2 py-1 text-sm border border-neutral-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 ${isRateDisabled ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed' : ''}`}
                          disabled={isProcessing || isRateDisabled}
                        />
                        )}
                        </ErrorIndicator>
                      </td>
                      );
                    })}
                    <td className="sticky right-0 z-10 bg-white px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => handleDeleteRow(row.id)}
                        disabled={isProcessing}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50"
                        title="Delete row"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
        </CardContent>
      </Card>

      {/* Error Banner — compact summary with "View Details" button */}
      {uploadErrors && (uploadErrors.errors.length > 0 || uploadErrors.failedRows > 0) && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-red-900">
                Processing Failed — {uploadErrors.failedRows} of {uploadErrors.totalRows} row{uploadErrors.totalRows !== 1 ? 's' : ''} failed
              </p>
              <p className="text-xs text-red-700 mt-0.5">
                All changes rolled back. Fix errors and resubmit.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowErrorModal(true)}
              className="text-red-700 border-red-300 hover:bg-red-100"
            >
              View Details
            </Button>
            {onDismissErrors && (
              <button
                type="button"
                onClick={handleDismissErrors}
                className="text-red-400 hover:text-red-600"
                aria-label="Dismiss errors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error Details Modal */}
      <Modal isOpen={showErrorModal} onClose={() => setShowErrorModal(false)} size="lg">
        <div className="bg-white rounded-lg p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-red-900">Processing Error Details</h3>
            <button
              type="button"
              onClick={() => setShowErrorModal(false)}
              className="text-neutral-400 hover:text-neutral-600"
              aria-label="Close error details"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Summary */}
          {uploadErrors && (
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-neutral-50 rounded-md text-center">
                <p className="text-2xl font-bold text-neutral-900">{uploadErrors.totalRows}</p>
                <p className="text-xs text-neutral-600">Total Rows</p>
              </div>
              <div className="p-3 bg-red-50 rounded-md text-center">
                <p className="text-2xl font-bold text-red-900">{uploadErrors.failedRows}</p>
                <p className="text-xs text-red-600">Failed</p>
              </div>
              <div className="p-3 bg-green-50 rounded-md text-center">
                <p className="text-2xl font-bold text-green-900">{uploadErrors.successfulRows || 0}</p>
                <p className="text-xs text-green-600">Successful</p>
              </div>
            </div>
          )}

          {/* Main error message + per-row details */}
          {uploadErrors && uploadErrors.errors.length > 0 && (() => {
            // Deduplicate and clean error messages, group by row
            const perRowErrors: { rowNumber: number; message: string }[] = [];
            const seenMessages = new Set<string>();

            // Collect from errors array
            for (const err of uploadErrors.errors) {
              let msg = err.message;
              const txMatch = msg.match(/Transaction failed and rolled back:\s*(.+)/);
              if (txMatch?.[1]) msg = txMatch[1];
              const liMatch = msg.match(/Failed to create line item:\s*(.+)/);
              if (liMatch?.[1]) msg = liMatch[1];
              if (msg.includes('Transaction rolled back due to error in batch')) continue;
              
              const key = `${err.rowNumber}:${msg}`;
              if (!seenMessages.has(key)) {
                seenMessages.add(key);
                perRowErrors.push({ rowNumber: err.rowNumber || 0, message: msg });
              }
            }

            // Also collect from processedRows
            if (uploadErrors.processedRows) {
              for (const row of uploadErrors.processedRows) {
                if (!row.success && row.errors) {
                  for (const e of row.errors) {
                    let msg = e.message;
                    if (msg.includes('Transaction rolled back')) continue;
                    const liMatch = msg.match(/Failed to create line item:\s*(.+)/);
                    if (liMatch?.[1]) msg = liMatch[1];
                    const key = `${row.rowNumber}:${msg}`;
                    if (!seenMessages.has(key)) {
                      seenMessages.add(key);
                      perRowErrors.push({ rowNumber: row.rowNumber, message: msg });
                    }
                  }
                }
              }
            }

            // Sort by row number
            perRowErrors.sort((a, b) => a.rowNumber - b.rowNumber);

            // If we have per-row errors with row numbers > 0, show the table
            const hasRowNumbers = perRowErrors.some(e => e.rowNumber > 0);

            return (
              <div className="space-y-3">
                {hasRowNumbers ? (
                  <div>
                    <p className="text-xs font-medium text-red-700 uppercase mb-2">Error Details by Row</p>
                    <div className="border border-red-200 rounded-md bg-white overflow-hidden max-h-64 overflow-y-auto">
                      <table className="min-w-full divide-y divide-red-100">
                        <thead className="bg-red-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase w-16">Row</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase w-32">Unit ID</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase">Error</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-red-50">
                          {perRowErrors.filter(e => e.rowNumber > 0).map((err, index) => {
                            // Extract unit ID from [ID] prefix if present
                            const idMatch = err.message.match(/^\[([^\]]+)\]\s*(.*)/);
                            const unitId = idMatch ? idMatch[1] : '—';
                            const msg = idMatch ? idMatch[2] : err.message;
                            return (
                              <tr key={index}>
                                <td className="px-4 py-2 text-sm text-neutral-900 font-medium">{err.rowNumber}</td>
                                <td className="px-4 py-2 text-sm text-neutral-700 font-mono">{unitId}</td>
                                <td className="px-4 py-2 text-sm text-red-600">{msg}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-xs font-medium text-red-700 uppercase mb-1">Errors</p>
                    {perRowErrors.map((err, index) => (
                      <p key={index} className="text-sm text-red-800">{err.message}</p>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
          <div className="flex justify-end pt-2">
            <Button type="button" variant="primary" onClick={() => setShowErrorModal(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* Submit Button */}
      <div className="flex justify-end space-x-3">
        <Button
          type="button"
          variant="outline"
          disabled={isProcessing}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSubmit}
          disabled={isProcessing || isSubmitted || marketsLoading || mediaFormatsLoading}
          isLoading={isProcessing}
        >
          {isProcessing ? 'Processing...' : isSubmitted ? 'Submitted' : 'Process Data'}
        </Button>
      </div>

      {/* Exception Modal - shown once per session */}
      <Modal
        isOpen={showExceptionModal}
        onClose={() => setShowExceptionModal(false)}
      >
        <div className="bg-white rounded-lg  p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">Exception Hold Notice</h3>
          <p className="text-sm text-neutral-600 mb-6">
            Checking this box allows you to request a hold expiration date beyond the standard 10 business day limit.
            Exception holds require approval before they can be processed by the charting team.
            You can add comments to explain why this exception is needed after the hold is created.
          </p>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="primary"
              onClick={() => setShowExceptionModal(false)}
            >
              OK
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

